import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateName, useSession } from '../lib/auth-client';
import './account.css';

const MAX_NAME_LENGTH = 40;

const Account: React.FC = () => {
  const navigate = useNavigate();
  const { data: session, isPending, error, refetch, isRefetching } = useSession();
  const [name, setName] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isPending && (!session?.user || error)) {
      navigate(`/login?returnTo=${encodeURIComponent('/account')}`);
    }
  }, [isPending, session, error, navigate]);

  useEffect(() => {
    if (session?.user) {
      setName(session.user.name ?? '');
    }
  }, [session?.user?.name]);

  useEffect(() => {
    if (!status) return;
    const timer = setTimeout(() => setStatus(null), 2500);
    return () => clearTimeout(timer);
  }, [status]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving) return;

    const trimmed = name.trim();

    if (!trimmed) {
      setSaveError('Display name is required.');
      setStatus(null);
      return;
    }

    if (trimmed.length > MAX_NAME_LENGTH) {
      setSaveError('Display name must be 40 characters or fewer.');
      setStatus(null);
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setStatus(null);

    try {
      await updateName(trimmed);
      await refetch({ query: { disableCookieCache: true } });
      setName(trimmed);
      setStatus('Display name updated.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to save your display name.';
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isPending) {
    return (
      <div className="account">
        <main className="account__card">
          <h1 className="account__title">Loading your account...</h1>
          <p className="account__subtitle">Shuffling the deck.</p>
        </main>
      </div>
    );
  }

  if (!session?.user) {
    return null;
  }

  return (
    <div className="account">
      <main className="account__card">
        <h1 className="account__title">Account</h1>
        <p className="account__subtitle">Keep your display name current for every session.</p>
        <form className="account__form" onSubmit={handleSubmit}>
          <div className="account__field">
            <label htmlFor="account-email" className="account__label">
              Email
            </label>
            <input
              id="account-email"
              type="email"
              className="account__input account__input--readonly"
              value={session.user.email ?? ''}
              readOnly
            />
          </div>
          <div className="account__field">
            <label htmlFor="account-name" className="account__label">
              Display name
            </label>
            <input
              id="account-name"
              type="text"
              className="account__input"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setSaveError(null);
                setStatus(null);
              }}
              autoComplete="name"
              disabled={isSaving}
            />
            <p className="account__helper">Max 40 characters. Leading/trailing spaces are ignored.</p>
          </div>
          <button type="submit" className="account__button" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save changes'}
          </button>
          {saveError ? (
            <p className="account__status account__status--error" role="alert">
              {saveError}
            </p>
          ) : null}
          {status ? (
            <p className="account__status account__status--success" role="status">
              {status}
            </p>
          ) : null}
          {isRefetching ? (
            <p className="account__status">Refreshing profile...</p>
          ) : null}
        </form>
      </main>
    </div>
  );
};

export default Account;
